import React, { useState, useEffect } from 'react';
import { Character, UpdateCharacterRequest } from '../types/character';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import './CharacterEdit.css';

interface CharacterEditProps {
  character: Character;
  onCharacterUpdated: () => void;
  onCancel: () => void;
}

interface CraftedItem {
  id: number;
  itemName: string;
  itemType: number; // 0=Weapon, 1=Armor, 2=Accessory, 3=Consumable, 4=Material, 5=Tool
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  speedBonus: number;
  durability: number;
  maxDurability: number;
  isEquipped: boolean;
  equippedToCharacterId: number | null;
  equippedToCharacterName: string | null;
}

const CharacterEdit: React.FC<CharacterEditProps> = ({ character, onCharacterUpdated, onCancel }) => {
  const { handleAuthError } = useAuth();
  const [formData, setFormData] = useState<UpdateCharacterRequest>({
    name: character.name,
    weaponSlot: character.weaponSlot || '',
    armorSlot: character.armorSlot || '',
    accessorySlot: character.accessorySlot || '',
  });
  const [craftedItems, setCraftedItems] = useState<CraftedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCraftedItems();
  }, []);

  const loadCraftedItems = async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/crafted-items');

      if (response.ok) {
        const data = await response.json();
        setCraftedItems(data);
        
        // Update form data based on equipped items
        const equippedToThisCharacter = data.filter((item: CraftedItem) => 
          item.isEquipped && item.equippedToCharacterId === character.id
        );
        
        // Find equipped items by type
        const weaponItem = equippedToThisCharacter.find((item: CraftedItem) => item.itemType === 0);
        const armorItem = equippedToThisCharacter.find((item: CraftedItem) => item.itemType === 1);
        const accessoryItem = equippedToThisCharacter.find((item: CraftedItem) => item.itemType === 2);
        
        // Update form data with equipped items
        setFormData(prev => ({
          ...prev,
          weaponSlot: weaponItem ? weaponItem.id.toString() : '',
          armorSlot: armorItem ? armorItem.id.toString() : '',
          accessorySlot: accessoryItem ? accessoryItem.id.toString() : ''
        }));
      } else {
        console.error('Failed to load crafted items');
      }
    } catch (error) {
      const apiError = handleApiError(error);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      console.error('Error loading crafted items:', apiError.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`/characters/${character.id}/update`, {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        onCharacterUpdated();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update character');
      }
    } catch (error) {
      const apiError = handleApiError(error);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEquipmentChange = (slot: 'weaponSlot' | 'armorSlot' | 'accessorySlot', itemId: string) => {
    setFormData(prev => ({ ...prev, [slot]: itemId }));
  };

  const getAvailableItems = (itemType: 'weapon' | 'armor' | 'accessory') => {
    // ItemType enum: 0=Weapon, 1=Armor, 2=Accessory, 3=Consumable, 4=Material, 5=Tool
    const itemTypeMap = {
      weapon: 0,
      armor: 1,
      accessory: 2
    };
    
    return craftedItems.filter(item => 
      item.itemType === itemTypeMap[itemType] && 
      (!item.isEquipped || item.equippedToCharacterId === character.id)
    );
  };

  const getEquippedItem = (slot: string) => {
    if (!slot) return null;
    return craftedItems.find(item => item.id.toString() === slot);
  };

  const calculateTotalStats = () => {
    const weaponItem = getEquippedItem(formData.weaponSlot || '');
    const armorItem = getEquippedItem(formData.armorSlot || '');
    const accessoryItem = getEquippedItem(formData.accessorySlot || '');

    const totalBonuses = {
      attack: (weaponItem?.attackBonus || 0) + (armorItem?.attackBonus || 0) + (accessoryItem?.attackBonus || 0),
      defense: (weaponItem?.defenseBonus || 0) + (armorItem?.defenseBonus || 0) + (accessoryItem?.defenseBonus || 0),
      hp: (weaponItem?.hpBonus || 0) + (armorItem?.hpBonus || 0) + (accessoryItem?.hpBonus || 0),
      mp: (weaponItem?.mpBonus || 0) + (armorItem?.mpBonus || 0) + (accessoryItem?.mpBonus || 0),
      speed: (weaponItem?.speedBonus || 0) + (armorItem?.speedBonus || 0) + (accessoryItem?.speedBonus || 0),
    };

    return totalBonuses;
  };

  const totalBonuses = calculateTotalStats();

  return (
    <div className="character-edit">
      <h2>Edit Character: {character.name}</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit} className="character-edit-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Equipment</h3>
          
          <div className="equipment-grid">
            <div className="equipment-slot">
              <label>Weapon:</label>
              <select
                value={formData.weaponSlot || ''}
                onChange={(e) => handleEquipmentChange('weaponSlot', e.target.value)}
              >
                <option value="">No weapon equipped</option>
                {getAvailableItems('weapon').map(item => (
                  <option key={item.id} value={item.id.toString()}>
                    {item.itemName} (ATK+{item.attackBonus}, DEF+{item.defenseBonus})
                  </option>
                ))}
              </select>
            </div>

            <div className="equipment-slot">
              <label>Armor:</label>
              <select
                value={formData.armorSlot || ''}
                onChange={(e) => handleEquipmentChange('armorSlot', e.target.value)}
              >
                <option value="">No armor equipped</option>
                {getAvailableItems('armor').map(item => (
                  <option key={item.id} value={item.id.toString()}>
                    {item.itemName} (ATK+{item.attackBonus}, DEF+{item.defenseBonus})
                  </option>
                ))}
              </select>
            </div>

            <div className="equipment-slot">
              <label>Accessory:</label>
              <select
                value={formData.accessorySlot || ''}
                onChange={(e) => handleEquipmentChange('accessorySlot', e.target.value)}
              >
                <option value="">No accessory equipped</option>
                {getAvailableItems('accessory').map(item => (
                  <option key={item.id} value={item.id.toString()}>
                    {item.itemName} (ATK+{item.attackBonus}, DEF+{item.defenseBonus})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Character Stats</h3>
          <div className="stats-grid">
            <div className="stat-row">
              <span>Base Stats:</span>
              <div className="base-stats">
                <span>VIT: {character.vitality}</span>
                <span>INT: {character.intelligence}</span>
                <span>STR: {character.strength}</span>
                <span>DEX: {character.dexterity}</span>
                <span>CON: {character.constitution}</span>
                <span>WIS: {character.wisdom}</span>
              </div>
            </div>
            
            <div className="stat-row">
              <span>Equipment Bonuses:</span>
              <div className="equipment-bonuses">
                <span className={totalBonuses.attack > 0 ? 'positive' : ''}>
                  ATK: +{totalBonuses.attack}
                </span>
                <span className={totalBonuses.defense > 0 ? 'positive' : ''}>
                  DEF: +{totalBonuses.defense}
                </span>
                <span className={totalBonuses.hp > 0 ? 'positive' : ''}>
                  HP: +{totalBonuses.hp}
                </span>
                <span className={totalBonuses.mp > 0 ? 'positive' : ''}>
                </span>
                <span className={totalBonuses.speed > 0 ? 'positive' : ''}>
                  SPD: +{totalBonuses.speed}
                </span>
              </div>
            </div>
            
            <div className="stat-row">
              <span>Derived Stats:</span>
              <div className="derived-stats">
                <span>HP: {character.currentHP}/{character.maxHP + totalBonuses.hp}</span>
                <span>AP: {character.actionPoints}</span>
                <span>Move: {character.movementPoints + Math.floor(totalBonuses.speed/2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CharacterEdit;